import { zodResolver } from '@hookform/resolvers/zod';
import Cookie from 'js-cookie';
import moment from 'moment';
import { FC, useCallback, useMemo, useState } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';

import { BasketDrawerConnected } from '@common/components/BasketDrawer';
import { Spinner } from '@common/components/Spinner';
import { Routes } from '@common/constants';
import {
  getGenderOptionsForRestriction,
  normaliseGenderCode,
} from '@common/helpers/genderRestriction';
import { useGetSpaClasses } from '@features/spa/hooks/useGetSpaClasses';
import { useGetSpaClassesPage } from '@features/spa/hooks/useGetSpaClassesPage';
import { useGetSpaSettings } from '@features/spa/hooks/useGetSpaSettings';
import { useTranslations } from '@lang';
import { createBasket, updateBasket } from '@store/basket/actions';
import { basketSelectors } from '@store/basket/selectors';
import { guestSelectors } from '@store/guest/selectors';
import { entitySelectors } from '@store/entity/selectors';
import { buildExistingGuests } from '@utils/buildExistingGuests';
import { useExistingGuestSelect } from '@utils/useExistingGuestSelect';
import { useRouter } from 'shared-routing';

import { Guest } from 'shared-types/Basket';
import { SpaClassBookingForm, SpaClassGuestConfig } from 'voyager';

import {
  SpaClassBookingFormValues,
  SpaClassBookingV2Props,
  spaClassBookingFormSchema,
} from './SpaClassBookingV2.types';

export const SpaClassBookingV2: FC<SpaClassBookingV2Props> = ({
  id,
  property,
  language,
  startDateTime,
}) => {
  const { t } = useTranslations();
  const router = useRouter();
  const dispatch = useDispatch();
  const [isBasketDrawerOpen, setIsBasketDrawerOpen] = useState(false);

  const loggedInGuest = useSelector(guestSelectors.getGuest) as Guest | null;
  const showGenderNeutral = useSelector(entitySelectors.isSpaShowGenderNeutralEnabled);
  const basketGuests = (useSelector((state: any) => basketSelectors.getBasketGuests(state)) ||
    []) as Guest[];
  const isSubmitting = useSelector((state: Record<string, unknown>) =>
    basketSelectors.getIsBasketDetailsFetching(state)
  ) as boolean;
  const currentBasketItems = useSelector(
    (state: Record<string, unknown>) =>
      (basketSelectors.getBasketDetails(state) as { items?: unknown[] })?.items || []
  );

  const arrivalDate = startDateTime
    ? moment(startDateTime).format('YYYY-MM-DD')
    : moment().format('YYYY-MM-DD');

  const { data: classes = [], isFetching } = useGetSpaClasses(property.id, arrivalDate);
  const { data: classesPage } = useGetSpaClassesPage(property.id, language);
  const { data: spaSettings } = useGetSpaSettings(property.id);
  const hideArrivalTimes = spaSettings?.hideArrivalTimes || false;
  const propertyModules = Object.keys(property.modulesExcludingDisabled || {});

  const spaClass = useMemo(() => classes.find((c) => c.id === id), [classes, id]);
  const timeslot = useMemo(
    () =>
      startDateTime && spaClass
        ? spaClass.availability?.find(
            (a) => moment(a.startDateTime).isSame(moment(startDateTime))
          )
        : undefined,
    [spaClass, startDateTime]
  );

  const isUnavailable =
    !isFetching && (!spaClass || !timeslot || timeslot.numberOfSpacesAvailable === 0);
  const isTelephoneOnly = false;

  const handleBack = useCallback(() => {
    router.push({ pathname: Routes.SPA_CLASSES_LIST });
  }, [router]);

  const existingGuests = useMemo(
    () => buildExistingGuests(basketGuests, loggedInGuest),
    [basketGuests, loggedInGuest]
  );

  const defaultGender =
    timeslot?.gender === 'M' ? 'male' : timeslot?.gender === 'F' ? 'female' : '';

  const form = useForm<SpaClassBookingFormValues>({
    resolver: zodResolver(spaClassBookingFormSchema),
    defaultValues: {
      guests: [{ title: '', firstName: '', lastName: '', gender: defaultGender }],
    },
    mode: 'onChange',
  });

  const watchedGuests = useWatch({ control: form.control, name: 'guests' });

  const guestConfigs: SpaClassGuestConfig[] = useMemo(() => {
    const guests = watchedGuests || [];
    return guests.map((g, i) => ({
      guestNumber: i + 1,
      values: {
        title: g?.title || '',
        firstName: g?.firstName || '',
        lastName: g?.lastName || '',
        gender: g?.gender || '',
      },
      existingGuests,
      selectedGuestId: (g as any)?.selectedGuestId,
    }));
  }, [watchedGuests, existingGuests]);

  const genderOptions = useMemo(() => {
    const options = [
      { value: 'male', label: t('common.male') },
      { value: 'female', label: t('common.female') },
    ];
    if (showGenderNeutral) options.push({ value: 'neutral', label: t('common.genderNeutral') });
    return getGenderOptionsForRestriction(normaliseGenderCode(timeslot?.gender as any), options)
      .genderOpts;
  }, [showGenderNeutral, timeslot?.gender, t]);

  const handleGuestChange = (
    guestIndex: number,
    field: 'title' | 'firstName' | 'lastName' | 'gender',
    value: string
  ) => {
    const current = form.getValues('guests') || [];
    const updated = [...current];
    if (!updated[guestIndex]) {
      updated[guestIndex] = { title: '', firstName: '', lastName: '', gender: '' } as any;
    }
    updated[guestIndex] = { ...updated[guestIndex], [field]: value };
    form.setValue('guests', updated, { shouldValidate: true });
  };

  const handleExistingGuestSelect = useExistingGuestSelect({ form: form as any, existingGuests });

  const labels = useMemo(
    () => ({
      title: t('spaClasses.booking.title'),
      subtitle: t('spaClasses.booking.subtitle'),
      guestDetails: {
        subtitle: t('spa.booking.guestDetails.subtitle'),
        dividerText: t('spa.booking.guestDetails.divider'),
        titlePlaceholder: t('spa.booking.guestDetails.selectPlaceholder'),
        titleLabel: t('common.title'),
        firstNameLabel: t('common.firstName'),
        lastNameLabel: t('common.lastName'),
        genderLabel: t('common.gender'),
        guestTitleFormatter: () => '',
      },
      stickyFooter: {
        totalLabel: t('basket.total'),
        ctaLabel: t('spa.book'),
      },
      telephoneOnly: spaSettings?.phoneNumber
        ? {
            title: t('spa.telephoneOnly', { spaEntity: t('common.classes').toLowerCase() }),
            subtitle: t('treatment.callUsOn', { phoneNumber: spaSettings.phoneNumber }),
          }
        : undefined,
      unavailable: {
        title: t('common.unavailable'),
        subtitle: t('spa.package.unavailableDescription', {
          title: spaClass?.name || '',
          date: startDateTime ? moment(startDateTime).format('dddd Do MMMM YYYY') : '',
        }),
        ctaLabel: t('common.backToModuleRoot', { entity: t('app.spaClasses') }),
        onCtaClick: handleBack,
      },
    }),
    [t, spaSettings?.phoneNumber, spaClass?.name, startDateTime, handleBack]
  );

  const handleSubmit = form.handleSubmit(async (data) => {
    if (!timeslot || !spaClass) return;

    const guestDetails = data.guests.map((g) => ({
      title: g.title,
      firstName: g.firstName,
      lastName: g.lastName,
      gender: g.gender,
      ...(g.loggedInUser ? { loggedInUser: g.loggedInUser } : {}),
      ...(g.externalSpaId ? { externalSpaId: g.externalSpaId } : {}),
    }));

    const parsedItem = {
      quantity: 1,
      type: 'spa-classes',
      checkIn: moment(timeslot.startDateTime).format(),
      checkOut: moment(timeslot.endDateTime).format(),
      guestDetails,
      bookable: {
        ids: {
          id: spaClass.id,
          type: 'spa-classes',
        },
      },
    };

    const basketId = Cookie.get('basketId');

    try {
      if (basketId) {
        await dispatch(
          updateBasket(
            property.id,
            basketId,
            language,
            { items: [...currentBasketItems, parsedItem] },
            false,
            true
          ) as any
        );
      } else {
        await dispatch(
          createBasket(property.id, language, { items: [parsedItem] }, true) as any
        );
      }
      setIsBasketDrawerOpen(true);
    } catch {
      router.push({ pathname: Routes.SPA_CLASSES_LIST });
    }
  });

  const handleCloseBasketDrawer = useCallback(() => {
    setIsBasketDrawerOpen(false);
    router.push({ pathname: Routes.SPA_CLASSES_LIST });
  }, [router]);

  const price = timeslot?.price?.amount ?? spaClass?.price?.amount ?? 0;
  const currencyCode = timeslot?.price?.currencyCode || spaClass?.price?.currencyCode || 'GBP';
  const formValues = form.watch();
  const isValid = spaClassBookingFormSchema.safeParse(formValues).success;

  if (isFetching && !spaClass) {
    return <Spinner isActive showDelayMessage />;
  }

  const fallbackImageUrl = classesPage?.heroBackgroundImage?.url;

  return (
    <FormProvider {...form}>
      <Spinner showDelayMessage isActive={isSubmitting} />
      <SpaClassBookingForm
        imageUrl={spaClass?.image?.url || fallbackImageUrl}
        imageAlt={spaClass?.name}
        onBack={handleBack}
        isLoading={isFetching}
        telephoneOnly={isTelephoneOnly}
        unavailable={isUnavailable}
        guests={guestConfigs}
        onExistingGuestSelect={handleExistingGuestSelect}
        onGuestChange={handleGuestChange}
        titleOptions={['Mr', 'Mrs', 'Ms', 'Miss', 'Dr']}
        genderOptions={genderOptions}
        stickyFooter={
          !isUnavailable
            ? {
                title: spaClass?.name || '',
                totalAmount: price * (watchedGuests?.length || 1),
                currencyCode,
                onCtaClick: handleSubmit,
                ctaDisabled: !isValid,
              }
            : undefined
        }
        labels={labels}
      />
      <BasketDrawerConnected
        open={isBasketDrawerOpen}
        onClose={handleCloseBasketDrawer}
        propertyModules={propertyModules}
        hideArrivalTimes={hideArrivalTimes}
      />
    </FormProvider>
  );
};
